/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { IonIcon, IonButton } from '@ionic/react';
import { caretDown } from 'ionicons/icons';
import { Icon } from 'rsuite';
import {
  Select,
  Flex,
  Box,
  Icon as ChakraIcon,
  Text,
  IconButton,
} from '@chakra-ui/react';
import { BsPeopleFill } from 'react-icons/bs';
import { FiArrowDown } from 'react-icons/fi';
import { PromptState, Prompt } from '../../../@types';
import * as Constants from '../../../constants';
import { useEpisodeStore } from '../../../store';
import {
  useCurrentUser,
  usePromptComments,
  useSonarGroups,
  useAddPromptComment,
} from '../../../api';
import { Chat } from '../../chat/Chat';
import { CommentsWrapper } from '../../chat/ChatStyles';
import { WaveBackground } from '../../../components/waveBackground/WaveBackground';

moment.updateLocale('en', {
  relativeTime: Constants.momentJsLocale,
});

const CommentFeed: React.FC<{
  // user: User;
  prompt: Prompt;
  state: PromptState;
  sonarGroup: any;
}> = ({ prompt, state, sonarGroup }) => {
  const { data: user } = useCurrentUser();
  const { mutateAsync: addPromptComment, isLoading } = useAddPromptComment();
  const {
    data: comments,
    isLoading: commentsLoading,
    isFetching,
  } = usePromptComments(prompt.id, sonarGroup.id);
  const commentFeedRef = useRef<HTMLIonContentElement | null>(null);

  const isModerator =
    user && user.user_role_id === Constants.USER_ROLES.TEACHER;
  const self = sonarGroup.group_members.find(
    ({ user_id }: any) => user && user_id === user.id
  );
  const canMessage =
    self && self.status !== Constants.SONAR_GROUP_MEMBER_STATUS.MUTED;

  const scrollContent = () => {
    setTimeout(() => {
      if (state.commentsOpen && commentFeedRef && commentFeedRef.current) {
        commentFeedRef.current.scrollToBottom(500);
      }
    }, 500);
  };

  useEffect(() => {
    scrollContent();
  }, [state.responsesOpen, comments]);

  async function onSubmitComment(comment: string) {
    if (user) {
      await addPromptComment({
        prompt_id: prompt.id,
        user_id: user.id,
        sonar_group_id: sonarGroup.id,
        comment,
      });
    }
  }
  if (!user) return null;

  return (
    <Chat
      type="prompt"
      onSubmit={onSubmitComment}
      comments={comments}
      isLoading={commentsLoading}
      isModerator={isModerator}
      canMessage={canMessage}
    />
  );
};

export const PromptComments: React.FC<{
  togglePromptComments: () => void;
  state: PromptState;
  prompt: Prompt;
}> = ({ togglePromptComments, state, prompt }) => {
  const { commentsOpen } = state;
  const { episode } = useEpisodeStore((episodeState) => ({
    episode: episodeState.episode,
  }));
  const { data: user } = useCurrentUser();

  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);

  const {
    data: { active: groups } = {},
    isLoading,
    refetch: refetchGroups,
  } = useSonarGroups((episode && episode.id) || '', (user && user.id) || '');

  const currentGroup =
    currentGroupId &&
    groups &&
    !!groups.length &&
    groups.find(({ id }: { id: string }) => id === currentGroupId);

  useEffect(() => {
    if (!isLoading && groups && !!groups.length)
      setCurrentGroupId(groups[0].id);
  }, [isLoading, groups]);

  useEffect(() => {
    if (commentsOpen) {
      refetchGroups();
    }
  }, [commentsOpen]);

  return (
    <CommentsWrapper
      aria-hidden={!commentsOpen}
      className={`comments-wrapper ${commentsOpen ? 'open' : ''}`}
    >
      {prompt && commentsOpen && (
        <>
          {/* <IonButton
              type="button"
              className="btn btn-close"
              color="primary"
              onClick={togglePromptComments}
            >
              <IonIcon icon={caretDown} />
            </IonButton> */}
          <Flex
            gap={3}
            px={6}
            pt={4}
            pb={6}
            bg="darkBlue.500"
            justifyContent="center"
            position="relative"
          >
            <Text fontSize={16} fontWeight={700} color="white">
              Connected community
            </Text>
            <IconButton
              position="absolute"
              right={2}
              top={2}
              variant="ghost"
              colorScheme="whiteAlpha"
              color="white"
              fontSize={24}
              onClick={togglePromptComments}
              aria-label="close"
              icon={<FiArrowDown />}
            />
          </Flex>
          <WaveBackground w="full" color="white" bg="white" p={4} />
          {!!groups?.length && (
            <Flex w="full" position="relative" zIndex={1} justify="flex-end">
              <Flex p={4} pt={0}>
                <Flex alignItems="center" gap={2}>
                  <ChakraIcon
                    as={BsPeopleFill}
                    fontSize={24}
                    color="darkBlue.500"
                  />
                  <Select
                    onChange={(e) => setCurrentGroupId(e.target.value)}
                    maxW="250px"
                    mt={0}
                    borderRadius="full"
                    bg="gray.50"
                  >
                    {groups.map((group: any) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </Flex>
            </Flex>
          )}
          {currentGroup ? (
            <CommentFeed
              sonarGroup={currentGroup}
              prompt={prompt}
              state={state}
            />
          ) : (
            <Flex
              flexDir="column"
              w="full"
              h="250px"
              align="center"
              justify="center"
              px={4}
              textAlign="center"
            >
              <Box mb={5} opacity={0.5} color="primary.500">
                <Icon size="3x" icon="podcast" />
              </Box>
              <Box color="gray.500">
                Create a sonar learning group to start commenting!
              </Box>
            </Flex>
          )}
        </>
      )}
    </CommentsWrapper>
  );
};
